/**
 * `ez.a.set_column` / `ez.a.clear_column` — the generic "set this column to this value" and
 * "clear this column" actions.
 *
 * monday has one block per column family (`41` status, `380` date, `50` people, …); these two
 * cover the rest without one block per type, which is what keeps a new column type from needing
 * a new automation block. The value is handed to the service layer untouched, so the column
 * type's own write transform owns its shape.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { interpolateTokens } from '../tokens.js';
import { columnRefSchema } from '../values.js';
import { tokenContextFor } from './token-context.js';
export const setColumnParams = z.object({
    columnId: columnRefSchema,
    value: z.unknown(),
});
export const setColumnAction = defineAction({
    id: 'ez.a.set_column',
    kind: 'action',
    app: 'monday',
    sentencePartial: 'set {column, columnId} to {value, value}',
    paramsSchema: setColumnParams,
    executable: true,
    references: (params) => [{ kind: 'column', id: params.columnId.columnId, invalid: params.columnId.invalid }],
    async execute(context, params) {
        if (context.itemId === null)
            return { skipped: 'no_item' };
        const raw = params.value;
        const value = typeof raw === 'string' ? interpolateTokens(raw, await tokenContextFor(context, [raw])) : raw;
        const result = await context.services.setCellValue({
            boardId: context.boardId,
            itemId: context.itemId,
            columnId: params.columnId.columnId,
            value,
        });
        return { columnId: params.columnId.columnId, changed: result.changed };
    },
});
export const clearColumnParams = z.object({ columnId: columnRefSchema });
export const clearColumnAction = defineAction({
    id: 'ez.a.clear_column',
    kind: 'action',
    app: 'monday',
    sentencePartial: 'clear {column, columnId}',
    paramsSchema: clearColumnParams,
    executable: true,
    references: (params) => [{ kind: 'column', id: params.columnId.columnId, invalid: params.columnId.invalid }],
    async execute(context, params) {
        if (context.itemId === null)
            return { skipped: 'no_item' };
        const result = await context.services.setCellValue({
            boardId: context.boardId,
            itemId: context.itemId,
            columnId: params.columnId.columnId,
            value: null,
        });
        return { columnId: params.columnId.columnId, changed: result.changed };
    },
});
//# sourceMappingURL=ez.a.set-column.js.map